import React, { useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  IconButton,
  Divider,
  Box,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { remCalc } from '../../common/utilities/style-util';

const styles = (theme) => ({
  feedbackModalContainer: {
    margin: '40px',
    position: 'absolute',
    top: 0,
    height: '100vh',
    overflowY: 'scroll',
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    color: theme.globalColors.white,
    padding: remCalc(50),
    [theme.breakpoints.down('sm')]: {
      marginBlock: remCalc(20),
      marginInline: remCalc(10),
      paddingBlock: remCalc(40),
      paddingInline: remCalc(20),
      maxWidth: 'calc(100% - 20px)',
    },
    '& ul': {
      [theme.breakpoints.down('sm')]: {
        paddingLeft: remCalc(20),
      },
    },
  },
  feedbackModalContent: {
    padding: 0,
    fontSize: remCalc(20),
    lineHeight: 2,
    [theme.breakpoints.down('sm')]: {
      lineHeight: 1.7,
    },
  },
  divider: {
    backgroundColor: theme.globalColors.mediumGrey,
    height: 2,
    marginBottom: remCalc(40),
    [theme.breakpoints.down('sm')]: {
      marginBottom: remCalc(30),
    },
  },
  closeButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    color: theme.globalColors.white,
    backgroundColor: theme.globalColors.mediumGrey,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
    },
  },
  titleContainer: {
    padding: 0,
  },
  title: {
    fontSize: remCalc(40),
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
      fontSize: remCalc(28),
    },
  },
  buttonWrapper: {
    marginTop: remCalc(50),
  },
  button: {
    paddingInline: remCalc(50),
  },
  listText: {
    [theme.breakpoints.down('sm')]: {
      fontSize: remCalc(16),
      overflowWrap: 'break-word',
    },
  },
  subtitle: {
    fontSize: remCalc(22),
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
      fontSize: remCalc(18),
    },
  },
  list: {
    marginBottom: remCalc(40),
  },
  boldLastTip: {
    '&:last-of-type': {
      fontWeight: 'bold',
    },
  },
  emailImageContainer: {
    padding: remCalc(50),
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      overflowX: 'scroll',
      whiteSpace: 'nowrap',
    },
  },
  emailImage: {
    maxWidth: '100%',
    display: 'block',
    marginInline: 'auto',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '150%',
      display: 'inline-block',
    },
  },
});

const LoginTrugreenIssuesModal = ({
  handleCloseModal,
  classes,
}) => {
  const corporateEmployeeTips = [
    'Enter your EmpowerID / Employee # / JDE#.',
    'New associates can access the Platform on day 1 (and up to 3 days) without a password.',
    'Password resets must be routed to the TruGreen Help Desk at 1-800-456-LAWN (5296). Request an EmpowerID password reset.',
    'EmpowerID credentials with username, password, and password reset instructions are sent within 24 hours after a new hire\'s start date based on when the manager marks the "Show" task in Workday. If completed after 12pm CST credentials will be sent the next day. Credentials for associates returning from leave may take an additional 24 hours.',
    'Credentials are sent via email to the TruGreen Corporate Employee\'s Supervisor OR first listed manager in Workday (alphabetically by last name) if multiple.',
    'Managers should check their Inbox, Spam, and Junk folders for the credentials email from TGAccessManagement@trugreenmail.com; the email subject includes the phrase “Login Credentials“ as highlighted in the sample email below.',
  ];

  const franchiseAssociateTips = [
    'Enter your Email and use the Temporary password provided by your Manager/IT team.',
    'Contact your respective branch\'s IT support team for any password reset questions or issues.',
  ];

  const emailImage = {
    src: 'https://s3.amazonaws.com/bright.public/trugreen-login-issues-sample-email.png',
    alt: 'Login Issues Sample Email',
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleCloseModal();
    }

    return null;
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, []);

  return (
    <div
      className={classes.feedbackModalContainer}
    >
      <IconButton
        aria-label="close"
        color="inherit"
        onClick={handleCloseModal}
        className={classes.closeButton}
        size="small"
      >
        <CloseIcon />
      </IconButton>
      <div
        className={classes.titleContainer}
      >
        <Typography
          component="h3"
          className={classes.title}
        >
          Quick Tips
        </Typography>
      </div>

      <Divider className={classes.divider} />

      <div className={classes.feedbackModalContent}>
        <Typography
          component="h4"
          variant="h5"
          className={classes.subtitle}
        >
          Corporate Employee Login Tips:
        </Typography>

        <ul className={classes.list}>
          {corporateEmployeeTips.map((tip, index) => (
            <li
              className={classes.listText}
              key={index}
            >
              {tip}
            </li>
          ))}
        </ul>

        <Typography
          component="h4"
          variant="h5"
          className={classes.subtitle}
        >
          Franchise Associate Login Tips:
        </Typography>

        <ul className={classes.list}>
          {franchiseAssociateTips.map((tip, index) => (
            <li
              className={classNames(classes.listText, classes.boldLastTip)}
              key={index}
            >
              {tip}
            </li>
          ))}
        </ul>

        <Box className={classes.emailImageContainer}>
          <img
            className={classes.emailImage}
            src={emailImage.src}
            alt={emailImage.alt}
          />
        </Box>

      </div>
    </div>
  );
};

export default withStyles(styles)(LoginTrugreenIssuesModal);

LoginTrugreenIssuesModal.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    feedbackModalContainer: PropTypes.string,
    feedbackModalContent: PropTypes.string,
    divider: PropTypes.string,
    closeButton: PropTypes.string,
    titleContainer: PropTypes.string,
    title: PropTypes.string,
    buttonWrapper: PropTypes.string,
    button: PropTypes.string,
    listText: PropTypes.string,
    subtitle: PropTypes.string,
    list: PropTypes.string,
    boldLastTip: PropTypes.string,
    emailImageContainer: PropTypes.string,
    emailImage: PropTypes.string,
  }).isRequired,
};
